import React, { useEffect, useState } from 'react';
import api from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';
import ConfirmModal from './ConfirmModal'; 
import TenantSettingsModal from './TenantSettingsModal';
import SuperAdminSessionManagement from './SuperAdminSessionManagement';

const SuperAdminDashboard = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [modalMode, setModalMode] = useState('edit'); // 'create' or 'edit'
  const [actionType, setActionType] = useState(''); // 'deactivate', 'activate', 'suspend'
  const { isAuthenticated, getAccessToken } = useAuth();

  useEffect(() => {
    fetchTenants();
  }, [isAuthenticated]);

  const fetchTenants = async () => {
    if (!isAuthenticated || !getAccessToken()) return;

    try {
      const res = await api.get('/tenant');
      setTenants(res.data);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Error fetching tenants');
    } finally {
      setLoading(false);
    }
  };

  // Open Create Tenant Modal
  const openCreateModal = () => {
    setSelectedTenant(null);
    setModalMode('create');
    setSettingsModalOpen(true);
  };

  // Open Edit Tenant Modal
  const openEditModal = (tenant) => {
    setSelectedTenant(tenant);
    setModalMode('edit');
    setSettingsModalOpen(true);
  };

  // Close Modal
  const closeModal = () => {
    setSettingsModalOpen(false);
    setSelectedTenant(null);
  };

  // Handle Save (both create and edit)
  const handleSave = (updatedTenant) => {
    if (modalMode === 'create') {
      // Add new tenant to list
      setTenants([...tenants, updatedTenant]);
    } else {
      // Update existing tenant in list
      setTenants(tenants.map(t => t._id === updatedTenant._id ? { ...t, ...updatedTenant } : t));
    }
  };

  // Open Status Change Confirmation
  const openStatusChangeModal = (tenant, action) => {
    setSelectedTenant(tenant);
    setActionType(action);
    setConfirmModalOpen(true);
  };

  // Change Tenant Status (Activate/Deactivate/Suspend)
  const changeStatus = async () => {
    if (!selectedTenant) return;

    try {
      let updateData = {};
      
      if (actionType === 'activate') {
        updateData = { isActive: true };
      } else if (actionType === 'deactivate') {
        updateData = { isActive: false };
      } else if (actionType === 'suspend') {
        updateData = { isActive: false, suspended: true };
      }

      await api.put(`/tenant/${selectedTenant._id}`, updateData);

      // Refresh tenant list
      fetchTenants();
    } catch (err) {
      console.error('Error changing tenant status:', err);
      setError('Error changing tenant status');
    } finally {
      setConfirmModalOpen(false);
      setSelectedTenant(null);
      setActionType('');
    }
  };

  // Get confirmation message based on action type
  const getConfirmMessage = () => {
    if (!selectedTenant) return '';
    
    switch (actionType) {
      case 'activate':
        return `Are you sure you want to activate "${selectedTenant.name}"?`;
      case 'deactivate':
        return `Are you sure you want to deactivate "${selectedTenant.name}"? Users will not be able to log in.`;
      case 'suspend':
        return `Are you sure you want to suspend "${selectedTenant.name}"? This tenant will be marked as suspended.`;
      default:
        return '';
    }
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">SuperAdmin Dashboard</h1>

      {error && <div className="bg-red-200 text-red-800 p-3 mb-6 rounded">{error}</div>}

      <button
        onClick={openCreateModal}
        className="mb-4 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
      >
        + Create New Tenant
      </button>

      {loading ? (
        <p>Loading tenants...</p>
      ) : (
        <table className="min-w-full bg-white border border-gray-200 text-left">
          <thead>
            <tr>
              <th className="py-2 px-4 border-b">Tenant Name</th>
              <th className="py-2 px-4 border-b">Type</th>
              <th className="py-2 px-4 border-b">Subscription</th>
              <th className="py-2 px-4 border-b">Status</th>
              <th className="py-2 px-4 border-b">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => (
              <tr key={tenant._id}>
                <td className="py-2 px-4 border-b">{tenant.name}</td>
                <td className="py-2 px-4 border-b">
                  <span className="text-xs px-2 py-1 rounded bg-gray-200">
                    {tenant.tenantType?.replace(/_/g, ' ').toUpperCase() || 'N/A'}
                  </span>
                </td>
                <td className="py-2 px-4 border-b">{tenant.subscriptionPlan || 'N/A'}</td>
                <td className="py-2 px-4 border-b">
                  {tenant.isActive ? (
                    <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800">
                      Active
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-800">
                      Inactive
                    </span>
                  )}
                </td>
                <td className="py-2 px-4 border-b">
                  <button
                    onClick={() => openEditModal(tenant)}
                    className="bg-blue-500 hover:bg-blue-600 text-white py-1 px-3 rounded mr-2"
                  >
                    Edit Settings
                  </button>
                  {tenant.isActive ? (
                    <button
                      onClick={() => openStatusChangeModal(tenant, 'deactivate')}
                      className="bg-orange-500 hover:bg-orange-600 text-white py-1 px-3 rounded"
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      onClick={() => openStatusChangeModal(tenant, 'activate')}
                      className="bg-green-500 hover:bg-green-600 text-white py-1 px-3 rounded"
                    >
                      Activate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Session Management */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">
            Session Management
          </h2>
          <span className="text-xs text-gray-400">
            Monitor active users and record locks
          </span>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <SuperAdminSessionManagement />
        </div>
      </div>

      {/* Confirmation Modal for Status Changes */}
      <ConfirmModal
        isOpen={confirmModalOpen}
        message={getConfirmMessage()}
        onConfirm={changeStatus}
        onCancel={() => setConfirmModalOpen(false)}
      />

      {/* Unified Tenant Settings Modal (Create & Edit) */}
      <TenantSettingsModal
        isOpen={settingsModalOpen}
        tenant={selectedTenant}
        mode={modalMode}
        onClose={closeModal}
        onSave={handleSave}
      />
    </div>
  );
};

export default SuperAdminDashboard;
